import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Download, Trash2, Mail } from "lucide-react";
import api, { formatApiError, cachedGet, apiCache } from "@/lib/api";
import { savePdf } from "@/lib/savePdf";
import { shareOrMailtoPdf } from "@/lib/sharePdf";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import EmailRecipientDialog from "@/components/EmailRecipientDialog";
import FieldHelp from "@/components/FieldHelp";

export default function VitalsPage() {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const [rows, setRows] = useState([]);
  const [recorders, setRecorders] = useState([]);
  const [open, setOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [form, setForm] = useState({ blood_pressure: "", pulse: "", bgl: "", oxygen: "", weight: "", notes: "", recorded_at: new Date().toISOString().slice(0, 16), recorded_by: "" });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = async () => {
    try { await cachedGet("/vitals", null, (data) => setRows(data)); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const loadRecorders = async () => {
    try {
      const { data } = await api.get("/vitals/recorders");
      setRecorders(data);
      const def = data.find((r) => r.is_default);
      if (def) setForm((f) => ({ ...f, recorded_by: f.recorded_by || def.name }));
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); loadRecorders(); }, []);

  const openDialog = () => {
    const def = recorders.find((r) => r.is_default);
    setForm((f) => ({ ...f, recorded_by: def ? def.name : f.recorded_by || (user?.full_name || "") }));
    setOpen(true);
  };

  const save = async () => {
    if (!form.recorded_by?.trim()) { toast.error("Please choose who recorded this reading"); return; }
    try {
      const payload = { ...form,
        pulse: parseFloat(form.pulse) || null, bgl: parseFloat(form.bgl) || null,
        oxygen: parseFloat(form.oxygen) || null, weight: parseFloat(form.weight) || null,
        recorded_at: new Date(form.recorded_at).toISOString(),
      };
      const r = await api.post("/vitals", payload);
      if (r.data?.status === "pending") toast("Saved — awaiting client approval");
      else toast.success("Vitals saved");
      setOpen(false);
      // Optimistic: splice the new record into the list instead of refetching.
      if (r.data?.id) setRows((prev) => [r.data, ...prev]);
      apiCache.invalidate("/vitals");
      apiCache.invalidate("/notifications");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get("/vitals/pdf", { responseType: "blob" });
      await savePdf(res.data, "vital_signs.pdf");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="vitals-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight">Vital Signs</h1>
          <p className="text-muted-foreground">Track BP, pulse, BGL, oxygen and weight readings.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadPdf} className="rounded-full font-bold gap-2 min-h-[48px]" data-testid="vitals-pdf-btn"><Download className="w-4 h-4" /> PDF</Button>
          <Button variant="outline" onClick={() => setEmailOpen(true)} className="rounded-full font-bold gap-2 min-h-[48px]" data-testid="vitals-email-btn"><Mail className="w-4 h-4" /> Email</Button>
          <Button onClick={openDialog} className="rounded-full font-bold gap-2 min-h-[48px]" style={{ background: "hsl(var(--client))" }} data-testid="add-vital-btn"><Plus className="w-4 h-4" /> Log reading</Button>
        </div>
      </header>

      <Card className="rounded-3xl border-2 overflow-hidden" data-testid="vitals-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider font-bold">
              <tr><th className="text-left p-3">When</th><th className="text-left p-3">BP</th><th className="text-left p-3">Pulse</th><th className="text-left p-3">BGL</th><th className="text-left p-3">O₂</th><th className="text-left p-3">Weight</th><th className="text-left p-3">By</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`vital-row-${r.id}`}>
                  <td className="p-3 font-bold text-xs">{r.recorded_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3">{r.blood_pressure}</td>
                  <td className="p-3">{r.pulse}</td>
                  <td className="p-3">{r.bgl}</td>
                  <td className="p-3">{r.oxygen ? `${r.oxygen}%` : ""}</td>
                  <td className="p-3">{r.weight ? `${r.weight}kg` : ""}</td>
                  <td className="p-3 text-xs">{r.recorded_by}</td>
                  <td className="p-3">
                    {isClient && <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={async () => { await api.delete(`/vitals/${r.id}`); load(); }} data-testid={`del-vital-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-muted-foreground">No readings yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="vital-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Log vital reading</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><F id="recorded_at" label="When" v={form.recorded_at} oc={upd("recorded_at")} type="datetime-local" /></div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider flex items-center">
                Recorded by *
                <FieldHelp text="Who actually took these readings — yourself, the worker on shift, or another carer. Required so the audit trail is clear." learnMoreId="vitals" />
              </Label>
              <Select value={form.recorded_by} onValueChange={(v) => setForm((f) => ({ ...f, recorded_by: v }))}>
                <SelectTrigger className="rounded-xl min-h-[48px]" data-testid="field-vital-recorded-by">
                  <SelectValue placeholder="Choose who took the reading" />
                </SelectTrigger>
                <SelectContent>
                  {recorders.map((r) => (
                    <SelectItem key={r.id || `${r.kind}-${r.name}`} value={r.name} data-testid={`vital-recorder-${r.kind}-${r.name.replace(/\s+/g, '-')}`}>
                      <div className="flex items-center justify-between gap-3 w-full">
                        <span>{r.name}</span>
                        <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${r.kind === "client" ? "bg-client/15 text-client" : "bg-worker/15 text-worker"}`}>
                          {r.kind}{r.is_default ? " · default" : ""}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <F id="bp" label="Blood pressure" v={form.blood_pressure} oc={upd("blood_pressure")} placeholder="120/80" />
            <F id="pulse" label="Pulse (bpm)" v={form.pulse} oc={upd("pulse")} type="number" />
            <F id="bgl" label="BGL (mmol/L)" v={form.bgl} oc={upd("bgl")} type="number" />
            <F id="oxygen" label="O₂ (%)" v={form.oxygen} oc={upd("oxygen")} type="number" />
            <F id="weight" label="Weight (kg)" v={form.weight} oc={upd("weight")} type="number" />
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={upd("notes")} className="rounded-xl" data-testid="field-vital-notes" />
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full font-bold" style={{ background: "hsl(var(--client))" }} data-testid="save-vital-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailRecipientDialog
        open={emailOpen} onOpenChange={setEmailOpen}
        title="Email vital signs report"
        description="Pick recipients from your address book or add new ones. The latest vital signs PDF will be attached."
        onSend={async (recipients) => {
          const res = await api.get("/vitals/pdf", { responseType: "blob" });
          const result = await shareOrMailtoPdf({
            blob: res.data,
            filename: "vital_signs.pdf",
            recipients,
            subject: "Vital signs report",
            body: `Hi,\n\nPlease find the latest vital signs report attached.\n\nKind regards,\n${user?.full_name || ""}`.trim(),
          });
          if (result === "shared") toast.success("Opened in your email app");
          else if (result === "mailto") toast.success(`PDF saved as “${"vital_signs.pdf"}” in Downloads. Attach it in the email composer that just opened.`, { duration: 12000 });
        }}
      />
    </div>
  );
}

function F({ id, label, v, oc, type = "text", placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase font-bold tracking-wider">{label}</Label>
      <Input value={v} onChange={oc} type={type} placeholder={placeholder} className="rounded-xl min-h-[48px]" data-testid={`field-${id}`} />
    </div>
  );
}
